import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import RotatingImage from '../RotatingImage';
import CapacityBadge from '../CapacityBadge';
import CoursePricePopover from './CoursePricePopover';
import { summarizeSchedule } from '../../utils/courseSchedule';
import type { IPricingSelection } from '../../utils/coursePricing';
import type { ICourse } from '../../utils/courseSchedule';
import { seasonLabel } from '../../utils/seasonLabel';

export type CourseMemberState = 'enrolled' | 'pending' | 'none';

interface CourseRowProps {
  course: ICourse;
  /** The currently selected member, or null when none chosen / logged out. */
  selectedMemberId: number | null;
  /** This member's enrollment state for this course. */
  state: CourseMemberState;
  submitting: boolean;
  /** `selection` carries a picked season stretch from the pricing modal. */
  onJoin: (course: ICourse, selection?: IPricingSelection) => void;
  onLeave: (course: ICourse) => void;
  /** Open the in-modal master→detail view for this course. */
  onOpenDetail: (course: ICourse) => void;
  /**
   * When true, this member just joined a paid open-enrollment course — show the
   * informational "complete payment" banner with the Pay-now bridge.
   */
  showPayPrompt?: boolean;
  /** Bridge to the choose-payment surface (member memberships page). */
  onGoToPayment?: () => void;
}

/**
 * Strip basic markdown to a flat preview string. Mirrors the helper used by
 * PublicEventsListPage so course descriptions read cleanly in the row body.
 */
const stripMarkdown = (md: string): string =>
  md
    .replace(/```[\s\S]*?```/g, '')
    .replace(/!\[.*?\]\(.*?\)/g, '')
    .replace(/\[([^\]]*?)\]\([^)]*?\)/g, '$1')
    .replace(/^#+\s*/gm, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/(\*\*|__)(?=\S)([\s\S]+?)(?<=\S)\1/g, '$2')
    .replace(/(?<![A-Za-z0-9_])([*_])(?=\S)([\s\S]+?)(?<=\S)\1(?![A-Za-z0-9_])/g, '$2')
    .replace(/~~(?=\S)([\s\S]+?)(?<=\S)~~/g, '$1')
    .replace(/\n{2,}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * A single course (kroužek) inside the activity detail modal, rendered as a
 * vertical poster card that mirrors the .event-card look from
 * PublicEventsListPage: rotating cover/gallery media on top (driven by the
 * course's brand colors) with a bottom gradient overlay anchoring the course
 * name, then a body with a compact schedule, a short stripped-markdown
 * description, an optional capacity hint, and a full-width per-member action
 * (Join / Enrolled + Leave / Request pending). Read-only when no member is
 * selected (e.g. logged-out users). The media/title and a "Details ›" link open
 * the in-modal course detail view via `onOpenDetail`.
 */
const CourseRow: React.FC<CourseRowProps> = ({
  course,
  selectedMemberId,
  state,
  submitting,
  onJoin,
  onLeave,
  onOpenDetail,
  showPayPrompt = false,
  onGoToPayment,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const { dayLabels, timeLabel, location, perEntry } = summarizeSchedule(
    course.schedule || [],
    locale
  );
  const hasSchedule = dayLabels.length > 0;

  const rawDescription =
    course.description?.trim() || course.extendedDescription?.trim() || '';
  const descriptionPreview = rawDescription ? stripMarkdown(rawDescription) : '';

  const primaryColor = course.colorSettings?.primaryColor || 'var(--orange-primary)';
  const secondaryColor = course.colorSettings?.secondaryColor || primaryColor;

  const enrolledCount = course.enrolledCount ?? 0;
  const isFull = course.capacity != null && enrolledCount >= course.capacity;

  const season = seasonLabel(course.termStart, course.termEnd, t, locale);
  const hasTerm = !!(course.termStart || course.termEnd);

  const cardStyle = {
    '--event-primary': primaryColor,
    '--event-secondary': secondaryColor,
    // Expired courses are dimmed (not hidden) so a season's history stays visible.
    ...(course.isExpired ? { opacity: 0.6 } : {}),
  } as React.CSSProperties;

  return (
    <div className="course-pcard" style={cardStyle}>
      <button
        type="button"
        className="course-pcard__media course-pcard__media-button"
        onClick={() => onOpenDetail(course)}
        aria-label={`${course.name} — ${t('courses.viewDetail')}`}
      >
        <RotatingImage
          coverImage={course.coverImage}
          gallery={course.gallery}
          seed={String(course._id)}
          alt={course.name}
        />
        <span
          className={`course-pcard__policy-badge course-pcard__policy-badge--${course.enrollmentPolicy}`}
        >
          {course.enrollmentPolicy === 'open'
            ? t('courses.enrollmentPolicyOpen')
            : t('courses.enrollmentPolicyApproval')}
        </span>
        <div className="course-pcard__media-overlay">
          <h4 className="course-pcard__title">{course.name}</h4>
          {(hasTerm || course.isExpired) && (
            <span className="course-pcard__season">
              {course.isExpired ? `${season} · ${t('courses.expired')}` : season}
            </span>
          )}
        </div>
      </button>

      <div className="course-pcard__body">
        {hasSchedule ? (
          <div className="course-pcard__schedule">
            {timeLabel ? (
              <>
                <div className="course-pcard__day-chips">
                  {dayLabels.map((day, i) => (
                    <span key={i} className="course-pcard__day-chip">
                      {day}
                    </span>
                  ))}
                </div>
                <div className="course-pcard__time-row">
                  <span className="course-pcard__time">{timeLabel}</span>
                  {location && (
                    <span className="course-pcard__location">{location} 📍</span>
                  )}
                </div>
              </>
            ) : (
              perEntry.map((entry, i) => (
                <div key={i} className="course-pcard__schedule-row">
                  <span className="course-pcard__day-chip">{entry.day}</span>
                  <span className="course-pcard__time">{entry.time}</span>
                  {entry.location && (
                    <span className="course-pcard__location">{entry.location} 📍</span>
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          <span className="course-pcard__no-schedule">{t('courses.noSchedule')}</span>
        )}

        <CapacityBadge enrolled={enrolledCount} capacity={course.capacity} />

        <div className="course-pcard__price">
          <CoursePricePopover
            course={course}
            selectedMemberId={selectedMemberId}
            state={state}
            isFull={isFull}
            submitting={submitting}
            onChoose={onJoin}
          />
        </div>

        {descriptionPreview && (
          <p className="course-pcard__description">{descriptionPreview}</p>
        )}

        <button
          type="button"
          className="course-pcard__detail-link"
          onClick={() => onOpenDetail(course)}
        >
          {t('courses.viewDetail')} ›
        </button>

        {showPayPrompt && state === 'enrolled' && onGoToPayment && (
          <div className="course-pcard__pay-prompt">
            <span className="course-pcard__pay-prompt-text">{t('courses.payPrompt')}</span>
            <Button variant="primary" fullWidth onClick={onGoToPayment}>
              {t('courses.payNow')}
            </Button>
          </div>
        )}

        {selectedMemberId != null && (
          <div className="course-pcard__action">
            {state === 'enrolled' ? (
              <>
                <span className="course-pcard__badge course-pcard__badge--enrolled">
                  {t('courses.enrolled')}
                </span>
                <Button
                  variant="danger"
                  fullWidth
                  onClick={() => onLeave(course)}
                  loading={submitting}
                  disabled={submitting}
                >
                  {t('courses.leave')}
                </Button>
              </>
            ) : state === 'pending' ? (
              <span className="course-pcard__badge course-pcard__badge--pending">
                {t('courses.pending')}
              </span>
            ) : (
              <button
                type="button"
                className="course-pcard__join-cta"
                onClick={() => onJoin(course)}
                disabled={submitting || isFull}
                title={isFull ? t('courses.full') : undefined}
              >
                {isFull
                  ? t('courses.full')
                  : submitting
                  ? `${t('courses.join')}…`
                  : t('courses.join')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseRow;
